/**
 * Reconciling a chat thread that arrives from more than one place.
 *
 * Messages reach the screen three ways — the socket, a catch-up read after a
 * gap, and the optimistic bubble the sender draws before the server has replied.
 * Any of them can deliver the same message, in any order, and the screen has to
 * end up with each message exactly once in the order it was written.
 *
 * Kept out of the screen so the rules are testable on their own: this is where
 * a duplicate bubble or an out-of-order thread would come from.
 */

/** Optimistic bubbles are drawn with a local id the server has never seen. */
const LOCAL_ID_PREFIX = "local-";

/**
 * How many messages one catch-up page asks for, and how many pages a caller will
 * walk before giving up and waiting for the next trigger.
 *
 * The ceiling stops a pathological gap spinning here; the server bounds the page
 * regardless, and anything still outstanding is picked up by the next reconnect
 * or resume. Shared so the two threads cannot drift to different limits.
 */
export const CATCH_UP_PAGE = 100;
export const CATCH_UP_MAX_PAGES = 20;

/**
 * The only shape these rules need. Deliberately structural rather than tied to
 * `ChatMessage`: one-to-one threads and small groups reconcile identically, and
 * two copies of this would be two chances to fix an ordering bug in one of them.
 */
type Mergeable = {
  id: string;
  timestamp: string;
};

/**
 * Merge without duplicating.
 *
 * Existing entries are replaced in place, so a reaction updates its bubble
 * rather than appending a second copy of the message it belongs to. Genuinely
 * new messages are added and the result re-sorted, because arrival order is not
 * send order: a live frame can land before the catch-up page covering the
 * messages behind it, which would otherwise leave the newer one sitting above
 * the older ones.
 *
 * The sort is stable, so messages written in the same instant keep the order the
 * server sent them in — the same tiebreak the server's own cursor relies on.
 */
export function mergeMessages<T extends Mergeable>(
  current: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) return current;

  const seen = new Map(current.map((message) => [message.id, message]));
  let changed = false;
  let appended = false;

  for (const message of incoming) {
    const existing = seen.get(message.id);
    if (!existing) {
      seen.set(message.id, message);
      changed = true;
      appended = true;
    } else if (existing !== message) {
      seen.set(message.id, message);
      changed = true;
    }
  }

  // Returning the same array when nothing moved keeps React from re-rendering
  // the thread on every empty catch-up.
  if (!changed) return current;

  const merged = [...seen.values()];
  return appended
    ? merged.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    : merged;
}

/**
 * The id a catch-up read should continue from.
 *
 * Optimistic bubbles are skipped: their ids are local inventions, and sending
 * one as a cursor would be rejected as a non-UUID — and would then be treated as
 * an unknown cursor, costing a full re-read of the conversation.
 */
export function lastServerMessageId(
  messages: Mergeable[],
): string | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const id = messages[index]?.id;
    if (id && !id.startsWith(LOCAL_ID_PREFIX)) return id;
  }
  return undefined;
}
